import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowUp, ArrowDown, Minus } from 'lucide-react';
import { clsx } from 'clsx';
import { useCategoryTranslation } from '../../hooks/useCategoryTranslation';
import type { AnalyticsPeriodResult } from '../../services/analytics';

interface ComparisonTableProps {
  periods: AnalyticsPeriodResult[];
}

function formatAmount(value: number, locale: string): string {
  return new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR' }).format(value);
}

function deltaPctLabel(baseline: number, comparison: number): { label: string; sign: 'up' | 'down' | 'flat' | 'na' } {
  if (baseline === 0) {
    if (comparison === 0) return { label: '—', sign: 'flat' };
    return { label: 'N/A', sign: 'na' };
  }
  const pct = ((comparison - baseline) / baseline) * 100;
  if (Math.abs(pct) < 0.5) return { label: '0 %', sign: 'flat' };
  return {
    label: `${pct > 0 ? '+' : ''}${pct.toFixed(1)} %`,
    sign: pct > 0 ? 'up' : 'down',
  };
}

function deltaIcon(sign: 'up' | 'down' | 'flat' | 'na', invert = false) {
  if (sign === 'flat' || sign === 'na') return <Minus className="w-3.5 h-3.5 text-gray-400" />;
  // invert = true pour les revenus (hausse = bien)
  const isPositive = invert ? sign === 'up' : sign === 'down';
  const colorClass = isPositive ? 'text-emerald-500' : 'text-rose-500';
  return sign === 'up' ? <ArrowUp className={clsx('w-3.5 h-3.5', colorClass)} /> : <ArrowDown className={clsx('w-3.5 h-3.5', colorClass)} />;
}

export default function ComparisonTable({ periods }: ComparisonTableProps) {
  const { t, i18n } = useTranslation('search');
  const { translateCategoryName } = useCategoryTranslation();
  const locale = i18n.language === 'en' ? 'en-GB' : 'fr-FR';

  // Union des catégories sur toutes les périodes
  const categoryRows = useMemo(() => {
    const map = new Map<string, { id: string; name: string; isDefault: boolean; type: 'income' | 'expense'; totals: number[] }>();
    periods.forEach((p, idx) => {
      for (const cat of p.byCategory) {
        const existing = map.get(cat.categoryId);
        if (existing) {
          existing.totals[idx] = cat.total;
        } else {
          const totals = new Array(periods.length).fill(0);
          totals[idx] = cat.total;
          map.set(cat.categoryId, {
            id: cat.categoryId,
            name: cat.name,
            isDefault: cat.isDefault,
            type: cat.type,
            totals,
          });
        }
      }
    });
    return Array.from(map.values()).sort((a, b) => {
      const aMax = Math.max(...a.totals);
      const bMax = Math.max(...b.totals);
      return bMax - aMax;
    });
  }, [periods]);

  const baselineIdx = 0;
  const comparisonIdx = periods.length - 1;

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100">
        <h3 className="text-sm font-semibold text-gray-800">{t('analytics.comparisonTableTitle')}</h3>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wide">
              <th className="px-4 py-3 text-left font-semibold">{t('analytics.col.category')}</th>
              {periods.map((p, idx) => (
                <th key={idx} className="px-4 py-3 text-right font-semibold">{p.label}</th>
              ))}
              <th className="px-4 py-3 text-right font-semibold">{t('analytics.col.delta')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {/* Totaux globaux */}
            {(['credits', 'debits', 'net'] as const).map((key) => {
              const baseline = periods[baselineIdx].totals[key];
              const comparison = periods[comparisonIdx].totals[key];
              const delta = deltaPctLabel(baseline, comparison);
              return (
                <tr key={key} className="bg-gray-50/40 font-medium">
                  <td className="px-4 py-3 text-gray-700">{t(`analytics.totals.${key}`)}</td>
                  {periods.map((p, idx) => (
                    <td key={idx} className="px-4 py-3 text-right whitespace-nowrap">
                      {formatAmount(p.totals[key], locale)}
                    </td>
                  ))}
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <span className="inline-flex items-center gap-1 text-xs">
                      {deltaIcon(delta.sign, key === 'credits' || key === 'net')}
                      {delta.label}
                    </span>
                  </td>
                </tr>
              );
            })}

            {/* Par catégorie */}
            {categoryRows.map((row) => {
              const baseline = row.totals[baselineIdx];
              const comparison = row.totals[comparisonIdx];
              const delta = deltaPctLabel(baseline, comparison);
              return (
                <tr key={row.id} className="hover:bg-gray-50/60">
                  <td className="px-4 py-3 text-gray-700">
                    <div className="flex items-center gap-2">
                      <span
                        className={clsx(
                          'inline-block w-1.5 h-1.5 rounded-full',
                          row.type === 'income' ? 'bg-emerald-500' : 'bg-rose-500',
                        )}
                      />
                      <span className="truncate">{translateCategoryName(row.id, row.name, row.isDefault)}</span>
                    </div>
                  </td>
                  {row.totals.map((total, idx) => (
                    <td key={idx} className="px-4 py-3 text-right text-gray-600 whitespace-nowrap">
                      {formatAmount(total, locale)}
                    </td>
                  ))}
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <span className="inline-flex items-center gap-1 text-xs">
                      {deltaIcon(delta.sign, row.type === 'income')}
                      {delta.label}
                    </span>
                  </td>
                </tr>
              );
            })}

            {categoryRows.length === 0 && (
              <tr>
                <td colSpan={periods.length + 2} className="px-4 py-6 text-center text-sm text-gray-500">
                  {t('analytics.noCategoryData')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
